import React, { useEffect, useMemo, useRef, useState } from "react"
import {
  AspectRatio,
  Box,
  BoxProps,
  Button,
  DrawerProps,
  Flex,
  Image,
  Text,
  VStack,
  useBreakpointValue,
  useDisclosure,
} from "@chakra-ui/react"

import { useRoutes } from "@app/hooks/useRoutes"
import { useInView } from "react-intersection-observer"
import { useAnalytics } from "@app/hooks/useAnalytics"
import { useConfigContext } from "@app/providers/config"
import { useShopifyPrice } from "@app/hooks/useShopify"
import { useCart } from "@app/hooks/useCart"
import { useCore } from "@app/hooks/useCore"
import { useWorlds } from "@app/hooks/useWorlds"
import { useShopifyImage } from "@app/hooks/useShopifyImage"
import { useProductComputed } from "@app/hooks/useProductComputed"
import { useProductBadges } from "@app/hooks/useProductBadges"
import { useOutOfStock } from "@app/hooks/useOutOfStock"
import Discount from "@app/components/Discount"
import ScavengerHunt from "@app/components/ScavengerHunt/ScavengerHunt"
import MotionBox from "@app/components/MotionBox"
import Icon from "@app/components/Icon"
import Link from "@app/components/Link"
import ProductCardWishlist from "@app/components/ProductCard/ProductCardWishlist"
import ResolvedImage from "@app/components/ResolvedImage"
import ProductBackInStock from "@app/components/Product/ProductBackInStock"
import QuickAddDrawer from "@app/components/ProductCard/QuickAddDrawer"
import ProductBadge from "@app/components/Product/ProductBadge"
import { LowStockMessage } from "@app/components/LowStockMessage"
import { Swiper, SwiperSlide } from "swiper/react"
import { useGlobalContext } from "@app/providers/global"

import { ReviewStarRating } from "@app/components/Product/ProductReviews/ReviewStarRating"
import { useDebounce } from "@app/hooks/useDebounce"
import { isGiftBundle } from "@app/utils/bundle"
import { scaleShopifyImage } from "@app/utils/image"
import { getOptionCount } from "@app/utils/product"
import { useLowStockThreshold } from "@app/hooks/useLowStockThreshold"

const ProductCardWishlistAnim = React.lazy(() => import("@app/components/ProductCard/ProductCardWishlistAnim"))

const productVariant = {
  open: {
    y: 0,
    opacity: 1,
    transition: {
      y: { stiffness: 1000, velocity: -100 },
    },
  },
  closed: {
    y: 10,
    opacity: 0,
    transition: {
      y: { stiffness: 1000 },
    },
  },
}

type Props = {
  item: any
  overrideImage?: any
  color?: string
  showTransition?: boolean
  pagePosition?: number
  handleTrackingClick?: () => void
  list?: string
  recentlyViewed?: boolean
  featured?: boolean
  wishlistId?: string
  isWishlistSharedPage?: boolean
  imageLoading?: "eager" | "lazy"
  disableLazyMounting?: boolean
  itemRef?: any
  addToCartButtonIcon?: React.ReactNode
  iconOnlyMobileCta?: boolean
  openInNewTab?: boolean
  addToCartCallback?: (product: any, variant: any) => void
  handleReactifyClick?: () => void
} & BoxProps

const ProductCard: React.FC<Props> = ({
  item,
  color,
  overrideImage,
  showTransition = true,
  handleTrackingClick,
  pagePosition,
  list,
  recentlyViewed,
  featured = false,
  wishlistId,
  isWishlistSharedPage = false,
  imageLoading = "lazy",
  disableLazyMounting = false,
  itemRef,
  addToCartButtonIcon,
  iconOnlyMobileCta = false,
  openInNewTab = false,
  addToCartCallback,
  handleReactifyClick,
  ...props
}) => {
  const wishlistRef = useRef(null)
  const isCarouselCard = recentlyViewed
  const [shouldPlayWishlistAnim, setShouldPlayWishlistAnim] = useState(false)
  const { getProductWorld } = useWorlds()

  const {
    helpers: { isBrowser },
  } = useCore()
  const {
    settings: { routes },
  } = useConfigContext()
  const { additionalStockOut } = useGlobalContext()
  const { isGiftCard } = useProductComputed(item)
  const isProductGiftBundle = isGiftBundle(item)

  const { trackProductImpression, trackQuickAddOpen } = useAnalytics()
  const { addToCart: addToCartStandard, loading: cartLoading } = useCart()
  const { urlResolver } = useRoutes()
  const [ref, inView] = useInView({ threshold: 0.3, triggerOnce: true })

  const { isOpen: quickAddDrawerisOpen, onOpen: quickAddDraweronOpen, onClose: quickAddDraweronClose } = useDisclosure()
  // Resolved here rather than inside the drawer: the drawer remounts on every open, so its own
  // `useBreakpointValue` would always return the "base" value on that first render.
  const quickAddPlacement = useBreakpointValue<DrawerProps["placement"]>({ base: "bottom", sm: "right" })
  const { isOpen: backInStockisOpen, onOpen: backInStockonOpen, onClose: backInStockonClose } = useDisclosure()
  const [backInStockisOpenDebounced, setBackInStockisOpenDebounced] = useState(false)
  const [quickAddDrawerisOpenDebounced, setQuickAddDrawerisOpenDebounced] = useState(false)
  const [primaryImageLoaded, setPrimaryImageLoaded] = useState(false)

  const addToCart = addToCartCallback || addToCartStandard

  // Local "adding" state so only THIS card shows the loading label — the shared
  // `cartLoading` flag is set for every button when any unrelated product is added.
  const [isAdding, setIsAdding] = useState(false)

  const handleAddToCart = async (product: any, variant: any, qty = 1) => {
    if (cartLoading || isAdding) return
    setIsAdding(true)
    try {
      // location "quick_add" flags PLP card adds on the GA4 add_to_cart event (Productive #4293); `list` is only set on PLP/search grid cards
      await addToCart(product, variant, qty, [], list ? "quick_add" : undefined)
    } finally {
      setIsAdding(false)
    }
  }

  useDebounce(() => (!backInStockisOpen ? setBackInStockisOpenDebounced(false) : null), 1000, [backInStockisOpen])
  // Keep the drawer mounted briefly after close so its slide-out transition can play.
  useDebounce(() => (!quickAddDrawerisOpen ? setQuickAddDrawerisOpenDebounced(false) : null), 1000, [quickAddDrawerisOpen])

  useEffect(() => {
    if (backInStockisOpen) {
      setBackInStockisOpenDebounced(true)
    }
  }, [backInStockisOpen])

  useEffect(() => {
    if (quickAddDrawerisOpen) {
      setQuickAddDrawerisOpenDebounced(true)
    }
  }, [quickAddDrawerisOpen])

  const [isTracked, setIsTracked] = useState(false) //for product impression tracking
  const { getBadgesByTags } = useProductBadges()
  const { stockStatus, isNotifyFlow, stockValue, isDisabledFlow, isInStoreOnly, isComingSoon } = useOutOfStock(item)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const liveActiveSibling = useMemo(() => ({ ...item, world: getProductWorld(item) }), [item])

  // TODO: the badge filter will be restored eventually
  // Restoring it drops the "In Store Only" badge, the only cue on the card and quick add drawer, neither of which renders a CTA.
  // const itemTags = useMemo(() => liveActiveSibling?.tags?.filter((tag: string) => tag.startsWith("badge:")), [liveActiveSibling])
  const badges = useMemo(() => getBadgesByTags(liveActiveSibling?.tags || []), [getBadgesByTags, liveActiveSibling?.tags])
  const { onSale, price, formattedPrice, formattedCompareAtPrice } = useShopifyPrice(liveActiveSibling?.variants?.[0])
  const { reviews } = useProductComputed(liveActiveSibling)
  const { scaledUrl: primaryImage } = useShopifyImage(liveActiveSibling?.images?.[0]?.src || liveActiveSibling?.image, "550x700")
  const { scaledUrl: secondaryImage } = useShopifyImage(liveActiveSibling?.images?.[1]?.src || liveActiveSibling?.image, "550x700")

  const siblingVariants = useMemo(() => (liveActiveSibling?.variants?.length > 0 ? liveActiveSibling?.variants : null), [liveActiveSibling])
  const url = useMemo(() => urlResolver(liveActiveSibling, routes.PRODUCT)?.url, [routes.PRODUCT, liveActiveSibling, urlResolver])

  const hasSiblingVariants = siblingVariants && siblingVariants.length > 1

  // options = self + the sibling scent group (live Sanity `siblings`, else Reactify `related`)
  const totalVariants = getOptionCount(item)
  const selectedVariant = useMemo(
    () => liveActiveSibling?.variants?.find((item: any) => item?.availableForSale) || liveActiveSibling?.variants?.[0],
    [liveActiveSibling]
  )

  const { lowStockSettings, showLowStockMessage } = useLowStockThreshold(siblingVariants?.[0]?.inventory_quantity, "plp")

  useEffect(() => {
    if (window.location.pathname.includes("collections") || window.location.pathname.includes("search")) {
      if (pagePosition && item && pagePosition <= 13 && !isTracked) {
        trackProductImpression(item, pagePosition)
        setIsTracked(true)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (window.location.pathname.includes("collections") || window.location.pathname.includes("search")) {
      if (!isTracked && inView) {
        trackProductImpression(item, pagePosition)
        setIsTracked(true)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inView])

  const isCarouselDisabledCue =
    (isCarouselCard || iconOnlyMobileCta) && (isDisabledFlow || (selectedVariant && !selectedVariant?.availableForSale))

  // Open the quick add drawer whenever there is more than one option to choose (sibling scent
  // group via `related`/`siblings`, or the product's own multiple variants). A single sellable
  // variant with no siblings adds straight to cart. Carousel cards never open the drawer.
  const hasQuickAddOptions = totalVariants > 1 || hasSiblingVariants

  const handleAddClick = () => {
    if (isCarouselCard || !hasQuickAddOptions) {
      handleAddToCart(liveActiveSibling, selectedVariant)
      return
    }
    if (list && !quickAddDrawerisOpen) trackQuickAddOpen(liveActiveSibling, pagePosition, list)
    quickAddDraweronOpen()
  }

  const overlayCtaDisabled = (!isNotifyFlow && selectedVariant && !selectedVariant?.availableForSale) || isDisabledFlow

  const handleOverlayCtaClick = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (cartLoading || isAdding) return
    if (isNotifyFlow) {
      backInStockonOpen()
    } else {
      handleAddClick()
    }
  }

  const overlayCtaLabel = (label: string) =>
    isComingSoon
      ? "Notify Me"
      : stockStatus
        ? stockStatus
        : selectedVariant && !selectedVariant?.availableForSale
          ? additionalStockOut
          : label

  const productImages = (liveActiveSibling?.images || []).filter((image: any) => image?.src)
  const swiperImages = productImages.length > 0 ? productImages : [{ src: liveActiveSibling?.image }]
  const showMobileImageCarousel = !isCarouselCard && !iconOnlyMobileCta && !overrideImage

  const addToCartControl = (
    <Button
      variant="onlyIcon"
      alignSelf="self-start"
      isLoading={isAdding}
      paddingInlineStart="0"
      paddingInlineEnd="0"
      boxSize="32px"
      minW={0}
      borderRadius="full"
      p={0}
      bg={isCarouselDisabledCue ? "grey.lighter" : "white"}
      border="1px solid"
      borderColor={isCarouselDisabledCue ? "border.lighter" : "border.lightest"}
      color={isCarouselDisabledCue ? "text.disabled" : undefined}
      cursor={cartLoading || isAdding ? "not-allowed" : "pointer"}
      disabled={
        isAdding ||
        isCarouselDisabledCue ||
        (!isNotifyFlow && selectedVariant && !selectedVariant?.availableForSale) ||
        stockValue === "in-store"
      }
      onClick={e => {
        e.preventDefault()
        e.stopPropagation()
        if (cartLoading || isAdding) return
        if (isNotifyFlow) {
          backInStockonOpen()
        } else {
          handleAddClick()
        }
      }}
    >
      <Flex alignItems="center" justifyContent="center">
        {addToCartButtonIcon ? <>{addToCartButtonIcon}</> : <Icon name="plusFaq" width="14px" height="14px" />}
      </Flex>
    </Button>
  )

  return item ? (
    <>
      {backInStockisOpenDebounced && (
        <ProductBackInStock onClose={backInStockonClose} isOpen={backInStockisOpen} product={item} location="quick_add" />
      )}
      {quickAddDrawerisOpenDebounced && (
        <QuickAddDrawer
          isOpen={quickAddDrawerisOpen}
          onClose={quickAddDraweronClose}
          item={liveActiveSibling}
          addToCartCallback={addToCartCallback}
          placement={quickAddPlacement}
        />
      )}
      <MotionBox
        ref={node => {
          ref(node)
          if (itemRef) {
            itemRef(node)
          }
        }}
        initial={showTransition ? "closed" : "open"}
        animate={inView && "open"}
        variants={productVariant}
        pos="relative"
        verticalAlign="top"
        {...props}
        height="100%"
      >
        {inView || !showTransition || disableLazyMounting ? (
          <Flex
            flexDirection="column"
            as={Link}
            to={url}
            tabIndex={0}
            onClick={e => {
              handleReactifyClick?.()
              handleTrackingClick?.()

              if (openInNewTab) {
                e.preventDefault()
                window.open(url, "_blank")
              }
            }}
            _hover={{ textDecoration: "none" }}
            role="group"
            width="100%"
            px={isCarouselCard ? { base: "0", md: "16px" } : { base: "1.5px", sm: "5px" }}
            py={isCarouselCard ? { base: "0", md: "16px" } : undefined}
            bg={isCarouselCard ? { base: "transparent", md: "white" } : undefined}
            borderWidth={isCarouselCard ? { base: "0", md: "1px" } : undefined}
            borderStyle={isCarouselCard ? "solid" : undefined}
            borderColor={isCarouselCard ? "border.lightest" : undefined}
            borderRadius={isCarouselCard ? { base: "0", md: "8px" } : undefined}
            justifyContent="space-between"
            height="100%"
          >
            <Box
              display={isCarouselCard ? "flex" : undefined}
              flexDirection={isCarouselCard ? "column" : undefined}
              flex={isCarouselCard ? "1" : undefined}
            >
              <Box position="relative">
                <Box
                  bg={featured ? "transparent" : isCarouselCard ? "primary.softGrey" : color || "grey.lightest"}
                  w="100%"
                  h="100%"
                  position="relative"
                  className="image"
                  borderRadius="7px"
                >
                  {showMobileImageCarousel && (
                    <Box display={{ base: "block", md: "none" }} borderRadius="7px" overflow="hidden">
                      <AspectRatio ratio={4 / 5}>
                        <Box sx={{ ".swiper": { width: "100%", height: "100%" }, ".swiper-slide": { width: "100%" } }}>
                          <Swiper slidesPerView={1}>
                            {swiperImages.map((image: any, index: number) => (
                              <SwiperSlide key={`product-card-mobile-grid-${index}`}>
                                <Image
                                  src={scaleShopifyImage(image?.src, "550x700")}
                                  alt={liveActiveSibling?.title}
                                  objectFit="cover"
                                  w="100%"
                                  h="100%"
                                  loading={index === 0 ? imageLoading : "lazy"}
                                />
                              </SwiperSlide>
                            ))}
                          </Swiper>
                        </Box>
                      </AspectRatio>
                    </Box>
                  )}
                  <Box display={showMobileImageCarousel ? { base: "none", md: "block" } : undefined}>
                    <AspectRatio ratio={featured ? 165 / 101 : 4 / 5}>
                      <Box p={overrideImage ? 0 : "7px"}>
                        {isCarouselCard && badges.length > 0 && <ProductBadge badges={badges} />}
                        <Box width="100%" height="100%">
                          {overrideImage ? (
                            <ResolvedImage
                              image={overrideImage}
                              w="100%"
                              h="100%"
                              objectFit="cover"
                              maxImageWidth={1000}
                              borderRadius="14px"
                              loading={imageLoading}
                            />
                          ) : (
                            <Box h="100%" pos="absolute" top={0} left={0} right={0} transition="all .7s ease-in-out">
                              <Image
                                src={primaryImage}
                                alt={liveActiveSibling?.title}
                                objectFit="cover"
                                w="100%"
                                h="100%"
                                zIndex={1}
                                transition="opacity 0.3s ease-in-out"
                                borderRadius="7px"
                                loading={imageLoading}
                                _groupHover={{
                                  opacity: secondaryImage ? 0 : 1,
                                }}
                                onLoad={() => setPrimaryImageLoaded(true)}
                              />

                              {secondaryImage && inView && primaryImageLoaded && (
                                <Image
                                  position="absolute"
                                  top="0"
                                  left="0"
                                  src={secondaryImage}
                                  alt={liveActiveSibling?.title}
                                  objectFit="cover"
                                  w="100%"
                                  h="100%"
                                  zIndex={2}
                                  opacity={0}
                                  transition="opacity 0.3s ease-in-out"
                                  borderRadius="7px"
                                  _groupHover={{ opacity: 1 }}
                                  loading={imageLoading}
                                />
                              )}
                            </Box>
                          )}
                        </Box>
                        {isCarouselCard && showLowStockMessage && (
                          <Box position="absolute" bottom={0} left={0} zIndex={98}>
                            <LowStockMessage settings={lowStockSettings} borderTopLeftRadius="0" />
                          </Box>
                        )}
                      </Box>
                    </AspectRatio>
                  </Box>
                </Box>

                <ScavengerHunt tags={liveActiveSibling?.tags} />
                {!isCarouselCard && badges.length > 0 && <ProductBadge badges={badges} />}
                {!isCarouselCard && !isGiftCard && !isProductGiftBundle && !isInStoreOnly && (
                  <>
                    <Box
                      display={{ base: "none", md: "block" }}
                      position="absolute"
                      bottom="16px"
                      left="50%"
                      transform="translateX(-50%)"
                      width="250px"
                      maxWidth="calc(100% - 28px)"
                      zIndex={99}
                      opacity={0}
                      visibility="hidden"
                      transition="opacity 0.2s ease-in-out"
                      _groupHover={{ opacity: 1, visibility: "visible" }}
                    >
                      <Button
                        variant="primary"
                        size="large"
                        width="100%"
                        height="48px"
                        opacity={0.97}
                        pointerEvents={cartLoading ? "none" : "auto"}
                        disabled={overlayCtaDisabled}
                        onClick={handleOverlayCtaClick}
                      >
                        {overlayCtaLabel("Quick Add")}
                      </Button>
                    </Box>
                    {iconOnlyMobileCta ? (
                      <Box display={{ base: "block", md: "none" }} position="absolute" bottom="8px" right="8px" zIndex={99}>
                        {addToCartControl}
                      </Box>
                    ) : (
                      <Button
                        variant="primary"
                        display={{ base: "inline-flex", md: "none" }}
                        position="absolute"
                        borderRadius="full"
                        borderWidth="1px"
                        borderColor="border.light"
                        backgroundColor="primary.white"
                        bottom="8px"
                        right="8px"
                        height="32px"
                        minW={0}
                        px="8px"
                        fontSize="12px"
                        zIndex={99}
                        opacity={0.97}
                        pointerEvents={cartLoading ? "none" : "auto"}
                        disabled={overlayCtaDisabled}
                        onClick={handleOverlayCtaClick}
                      >
                        <Flex alignItems="center" gap="2px" fontFamily="Graphik">
                          <Icon name="plusFaq" width="12px" height="12px" display="flex" />
                          Add
                        </Flex>
                      </Button>
                    )}
                  </>
                )}
                {isCarouselCard && !isGiftCard && !isProductGiftBundle && !isInStoreOnly && (
                  <Box position="absolute" bottom="8px" right="8px" zIndex={99}>
                    {addToCartControl}
                  </Box>
                )}
              </Box>
              <Flex
                className="detail"
                direction="column"
                py={isCarouselCard ? "12px" : "16px"}
                px="8px"
                flex={isCarouselCard ? "1" : undefined}
              >
                <VStack spacing="4px" alignItems="flex-start">
                  <Text variant="plpCardTitle" textDecoration="none">
                    {liveActiveSibling?.title}
                  </Text>
                  {price ? (
                    <Flex alignItems="center">
                      {onSale && (
                        <Text size="md" variant="medium" color="text.secondary" textDecoration="line-through" marginRight="4px">
                          {formattedCompareAtPrice}
                        </Text>
                      )}
                      <Text size="md" variant="medium" color="text.primary">
                        {formattedPrice}
                      </Text>
                    </Flex>
                  ) : null}
                  <Discount tags={liveActiveSibling?.tags} forceColor="text.sale" size="sm" lineHeight="16.8px" mb="0" />
                  {!isGiftCard && totalVariants > 1 && (
                    <Text fontFamily="Larsseit" fontSize="14px" lineHeight="19.6px" textDecoration="underline" color="text.placeholder">
                      {totalVariants} Options
                    </Text>
                  )}
                  {Number(reviews?.averageScore) >= 3.5 && (
                    <Box pt="4px">
                      <ReviewStarRating numOfStars={Number(reviews.averageScore)} size={3} />
                    </Box>
                  )}
                </VStack>
              </Flex>
            </Box>
          </Flex>
        ) : (
          <AspectRatio width="full" ratio={3 / 5}>
            <></>
          </AspectRatio>
        )}
        <Box
          pos="absolute"
          top={isCarouselCard ? { base: "0", md: "17px" } : "0"}
          right={isCarouselCard ? { base: "0", md: "17px" } : { base: "1.5px", sm: "5px" }}
          zIndex={2}
        >
          <Box ref={wishlistRef}>
            <ProductCardWishlist
              product={item}
              handleAdd={() => setShouldPlayWishlistAnim(true)}
              wishlistId={wishlistId}
              isWishlistSharedPage={isWishlistSharedPage}
            />
          </Box>
        </Box>

        {isBrowser && (
          <React.Suspense fallback={<div />}>
            <ProductCardWishlistAnim
              posRef={wishlistRef}
              shouldPlay={shouldPlayWishlistAnim}
              handlePlay={() => setShouldPlayWishlistAnim(false)}
            />
          </React.Suspense>
        )}
      </MotionBox>
    </>
  ) : null
}

export default React.memo(ProductCard)
